import { PhonogramManagerForm, PhonogramManagerFormRef } from "@/components/phonogram-manager-form";
import Grid from "@/components/wijmo-grid/grid/grid.vue";
import { PhonogramManagerLocale } from "@/locales/localeid";
import { PhonogramModel, PhonogramType } from "@/models/phonogram-manager-model";
import { VCSLevelModel } from "@/models/title-list-model";
import TitleListState from "@/modules/title-list";
import { VersionGroupModule } from "@/modules/versiongroup";
import { PhonogramManagerService } from "@/services/phonogram-manager-service";
import { AllLevel, Unit1Id } from "@/util/constants";
import { AccessLevel, CurriculumType } from "@/util/enum";
import { disabledDueToAccessLevel, getRouteAccessLevel } from "@/util/func";
import { Alert, Button, Checkbox, Col, Input, Radio, Row, Select } from "ant-design-vue";
import { ActionItem, isGuid } from "commonui";
import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";

@Component({
	components: {
		Grid,
		Row,
		Col,
		Input,
		RadioGroup: Radio.Group,
		Radio,
		Select,
		Option: Select.Option,
		Checkbox,
		Button,
		ActionItem,
		PhonogramManagerForm,
		Alert
	}
})
export default class PhonogramManager extends Vue {
	@Ref("formComponentRef") formComponentRef!: PhonogramManagerFormRef;

	public readonly locale = PhonogramManagerLocale;
	public readonly PhonogramType = PhonogramType;
	public readonly AccessLevel = AccessLevel;
	public readonly allLevel = AllLevel;

	public titleListModule = getModule(TitleListState);
	public versionGroupModule = VersionGroupModule;
	public phonogramTypeDescriptions: { [key: number]: string } = {
		[PhonogramType.Card]: this.$format(PhonogramManagerLocale.GridTypePhonogramCard),
		[PhonogramType.WordCard]: this.$format(PhonogramManagerLocale.GridTypePhonogramWordCard)
	};

	public filter: PhonogramManagerFilter = this.getDefaultFilterValues();
	public loading: boolean = false;
	private originalData: PhonogramModel[] = [];
	public filteredData: PhonogramModel[] = [];

	created() {
		this.onUpdateVersion();
	}

	mounted() {
		VersionGroupModule.setDisabled(false);
	}

	destroyed() {
		VersionGroupModule.setDisabled(true);
	}

	@Watch("versionGroupModule.selectedLsVersion")
	@Watch("versionGroupModule.selectedGsVersion")
	onUpdateVersion() {
		if (this.isAnyVersionSelected) {
			this.filter = this.getDefaultFilterValues();
			this.fetch();
		} else {
			this.originalData = [];
			this.filteredData = this.originalData;
		}
	}

	public get isAnyVersionSelected(): boolean {
		return this.isGSVersionSelected || this.isLSVersionSelected;
	}
	public get isGSVersionSelected(): boolean {
		return !!isGuid(this.versionGroupModule.selectedGsVersion);
	}
	public get isLSVersionSelected(): boolean {
		return !!isGuid(this.versionGroupModule.selectedLsVersion);
	}

	public get levels(): VCSLevelModel[] {
		/*
		 If selected GrapeSeed version is None the select only LittleSeed units.
		 If selected LittleSeed version is None the select only GrapeSeed units.
		 If both GrapeSeed and LittleSeed are None then return empty array.
		 If both GrapeSeed and LittleSeed are selected then return all the units.
		*/
		if (VersionGroupModule.selectedGsVersion == "-1" && VersionGroupModule.selectedLsVersion != "-1") {
			return this.titleListModule.levels.filter((l) => l.curriculumType != 0);
		} else if (VersionGroupModule.selectedLsVersion == "-1" && VersionGroupModule.selectedGsVersion != "-1") {
			return this.titleListModule.levels.filter((l) => l.curriculumType != 1);
		} else if (VersionGroupModule.selectedGsVersion == "-1" && VersionGroupModule.selectedLsVersion == "-1") {
			return [];
		} else {
			return this.titleListModule.levels;
		}
	}

	public get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	public get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	private getDefaultFilterValues(): PhonogramManagerFilter {
		let defaultUnitId: VCSLevelModel | undefined;

		if (this.isGSVersionSelected) {
			defaultUnitId = this.titleListModule.levels.find(
				(level) => level.curriculumType === CurriculumType.GrapeSEED
			);
		} else if (this.isLSVersionSelected) {
			defaultUnitId = this.titleListModule.levels.find(
				(level) => level.curriculumType === CurriculumType.LittleSEED
			);
		}

		return {
			keyword: "",
			level: defaultUnitId?.id || AllLevel,
			type: PhonogramType.All
		};
	}

	private fetch() {
		this.loading = true;
		PhonogramManagerService.getPhonograms({
			grapeSeedVersion:
				VersionGroupModule.selectedGsVersion !== "-1" ? VersionGroupModule.selectedGsVersion : null,
			littleSeedVersion:
				VersionGroupModule.selectedLsVersion !== "-1" ? VersionGroupModule.selectedLsVersion : null
		})
			.then((phonograms) => {
				phonograms.forEach((phonogram) => {
					phonogram.phonogramType = this.phonogramTypeDescriptions[phonogram.phonogramTypeId];
				});
				this.originalData = phonograms;
				this.filteredData = this.originalData;
				this.onFilter();
				this.loading = false;
			})
			.catch(() => {
				this.loading = false;
			});
	}

	public onFilter() {
		const keyword = this.filter.keyword.toLocaleLowerCase();

		this.filteredData = this.originalData.filter((phonogram, index) => {
			const { name, focusUnitId, maxReviewUnitId, phonogramTypeId } = phonogram;

			// Type filter
			if (this.filter.type && phonogramTypeId !== this.filter.type) {
				return false;
			}

			// Unit filter
			if (
				this.filter.level !== -1 &&
				!(focusUnitId === this.filter.level || maxReviewUnitId === this.filter.level)
			) {
				return false;
			}

			// Keyword filter
			if (name.toLocaleLowerCase().indexOf(keyword) !== -1) {
				return true;
			}

			return false;
		});
	}
	public onAddPhonogram() {
		this.formComponentRef.open();
	}
	public onEditPhonogram(phonogram: PhonogramModel) {
		this.formComponentRef.open(phonogram);
	}

	public reloadPhonograms() {
		this.fetch();
	}
}

interface PhonogramManagerFilter {
	keyword: string;
	level: number;
	type: PhonogramType;
}
